/**
 * PoiFormWizard.jsx
 * Formulario de Ponto de Interesse em Etapas Premium
 *
 * 6 Passos:
 * 1. Basico - Nome, Coordenadas, Descricao
 * 2. Grupo - Grupo, Favorito, Visibilidade
 * 3. Aparencia - Cor, Icone, Raio
 * 4. Localizacao - Mapa interativo
 * 5. Endereco e Contato - Campos de endereco e contato
 * 6. Revisao - Resumo antes de salvar
 */

import React, {
 useState, useCallback, useRef, useEffect 
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Info as InfoIcon,
  Palette as PaletteIcon,
  Map as MapIcon,
  LocationOn as LocationOnIcon,
  ContactPhone as ContactPhoneIcon,
  CheckCircle as ReviewIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { MapPin } from 'lucide-react';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { poisActions } from '../../store';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';
import SearchableSelect from '../../common/components/SearchableSelect';
import MapLocationPicker from '../../common/components/MapLocationPicker';
import reverseGeocode from '../../common/util/reverseGeocode';
import PoiColorPicker from '../PoiColorPicker';
import PoiIconSelector from '../PoiIconSelector';
import PoiMarkerPreview, { pascalToKebab } from '../PoiMarkerPreview';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            label={`${t('positionLatitude')} *`}
            value={data.latitude ?? ''}
            onChange={(e) => updateData({ latitude: parseFloat(e.target.value) || 0 })}
            error={!!errors?.latitude}
            helperText={errors?.latitude}
            inputProps={{ step: 'any' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            type="number"
            label={`${t('positionLongitude')} *`}
            value={data.longitude ?? ''}
            onChange={(e) => updateData({ longitude: parseFloat(e.target.value) || 0 })}
            error={!!errors?.longitude}
            helperText={errors?.longitude}
            inputProps={{ step: 'any' }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: GRUPO E VISIBILIDADE
// ============================================================================

const GroupStep = ({ data, updateData, groups }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  // Custom render para grupos com cor
  const renderGroupOption = (props, option) => {
    const { key, ...restProps } = props;
    if (option._isEmpty) {
      return (
        <Box component="li" {...restProps} key="empty" sx={{ color: colors.textMuted, fontStyle: 'italic', py: 1, px: 2 }}>
          {option.name}
        </Box>
      );
    }
    return (
      <Box
        component="li"
        {...restProps}
        key={option.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          '&:hover': { backgroundColor: `${colors.accentSubtle} !important` },
          '&[aria-selected="true"]': { backgroundColor: `${colors.accentHover} !important` },
        }}
      >
        {option.color && (
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: option.color,
              flexShrink: 0,
            }}
          />
        )}
        <Typography variant="body2">{option.name}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SearchableSelect
            options={groups}
            value={data.groupId}
            onChange={(value) => updateData({ groupId: value })}
            label={t('sharedGroup')}
            emptyOption={{ value: null, label: t('sharedNone') }}
            renderOption={renderGroupOption}
            startAdornment={<CategoryIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: colors.surfaceHover,
              borderRadius: tokens?.radius?.md || '8px',
              border: `1px solid ${colors.borderSubtle}`,
              px: 2,
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={data.favorite || false}
                  onChange={(e) => updateData({ favorite: e.target.checked })}
                  icon={<StarBorderIcon />}
                  checkedIcon={<StarIcon sx={{ color: colors.warning }} />}
                />
              }
              label={
                <Typography sx={{ color: colors.textPrimary, fontSize: '14px' }}>
                  {t('poiFavorite') || 'Favorito'}
                </Typography>
              }
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: colors.surfaceHover,
              borderRadius: tokens?.radius?.md || '8px',
              border: `1px solid ${colors.borderSubtle}`,
              px: 2,
              py: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={data.visible !== false}
                  onChange={(e) => updateData({ visible: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.accent,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.accent,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: colors.textPrimary, fontSize: '14px' }}>
                  {t('sharedShow')}
                </Typography>
              }
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('poiGroupHelp') || 'Grupos ajudam a organizar seus pontos de interesse. A visibilidade controla se o POI aparece no mapa.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 3: APARENCIA
// ============================================================================

const AppearanceStep = ({
  data,
  updateData,
  icons,
  selectedIcon,
  setSelectedIcon,
  iconSelectorOpen,
  setIconSelectorOpen,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleIconSelect = useCallback((iconData) => {
    setSelectedIcon(iconData);

    if (iconData.iconType === 'lucide') {
      updateData({
        iconId: null,
        iconType: 'lucide',
        iconCode: pascalToKebab(iconData.iconCode),
        color: data.color || iconData.suggestedColor,
      });
    } else {
      updateData({
        iconId: iconData.id,
        iconType: 'png',
        iconCode: null,
        color: data.color || iconData.suggestedColor,
      });
    }
  }, [data.color, updateData, setSelectedIcon]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box display="flex" gap={3} alignItems="flex-start" flexWrap="wrap">
        {/* Preview do marcador */}
        <Box
          sx={{
            p: 3,
            bgcolor: colors.surfaceElevated,
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${colors.borderSubtle}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 140,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textMuted, mb: 2 }}>
            {t('poiPreview') || 'Preview'}
          </Typography>
          <PoiMarkerPreview
            icon={selectedIcon}
            color={data.color || '#3B82F6'}
            name={data.name}
            size="large"
          />
        </Box>

        {/* Controles */}
        <Box display="flex" flexDirection="column" gap={3} flex={1}>
          {/* Cor do marcador */}
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ minWidth: 120, color: colors.textSecondary }}>
              {t('poiMarkerColor') || 'Cor do Marcador'}:
            </Typography>
            <PoiColorPicker
              value={data.color || '#3B82F6'}
              onChange={(color) => updateData({ color })}
            />
            <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace' }}>
              {data.color || '#3B82F6'}
            </Typography>
          </Box>

          {/* Seletor de icone */}
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ minWidth: 120, color: colors.textSecondary }}>
              {t('poiIcon') || 'Icone'}:
            </Typography>
            <Button
              variant="outlined"
              startIcon={<MapPin size={18} />}
              onClick={() => setIconSelectorOpen(true)}
              sx={{
                borderColor: colors.accent,
                color: colors.accent,
                borderRadius: tokens?.radius?.md || '8px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: colors.accentSubtle,
                  borderColor: colors.accent,
                },
              }}
            >
              {t('poiSelectIcon') || 'Selecionar Icone'}
            </Button>
            {selectedIcon && (
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {selectedIcon.iconType === 'lucide' ? selectedIcon.iconCode : selectedIcon.name}
              </Typography>
            )}
          </Box>

          {/* Raio de proximidade */}
          <PremiumInput
            type="number"
            value={data.radius ?? 100}
            onChange={(e) => updateData({ radius: parseInt(e.target.value, 10) || 100 })}
            label={t('poiRadius') || 'Raio de Proximidade'}
            sx={{ maxWidth: 220 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">m</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 10000 }}
          />
        </Box>
      </Box>

      {/* Dialog do seletor de icones */}
      <PoiIconSelector
        open={iconSelectorOpen}
        onClose={() => setIconSelectorOpen(false)}
        onSelect={handleIconSelect}
        selectedIconId={data.iconId}
        icons={icons}
      />
    </Box>
  );
};

// ============================================================================
// STEP 4: LOCALIZACAO NO MAPA
// ============================================================================

const LocationStep = ({ data, updateData, onLocationChange }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('poiLocationHelp') || 'Clique no mapa para definir a localizacao do ponto de interesse.'}
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: tokens?.radius?.lg || '12px',
          overflow: 'hidden',
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <MapLocationPicker
          latitude={data.latitude}
          longitude={data.longitude}
          onChange={onLocationChange}
          height={400}
          zoom={16}
        />
      </Box>

      {data.latitude && data.longitude && (
        <Box display="flex" gap={2} justifyContent="center">
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {t('positionLatitude')}: {data.latitude?.toFixed(6)}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {t('positionLongitude')}: {data.longitude?.toFixed(6)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// STEP 5: ENDERECO E CONTATO
// ============================================================================

const AddressContactStep = ({ data, updateData, geocodingLoading, onRefreshAddress }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Secao Endereco */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('poiAddress') || 'Endereco'}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <PremiumInput
                value={data.address || ''}
                onChange={(e) => updateData({ address: e.target.value })}
                label={t('poiAddressFull') || 'Endereco Completo'}
                placeholder="Rua, numero, bairro..."
                disabled={geocodingLoading}
              />
              <Tooltip title={t('poiRefreshAddress') || 'Atualizar endereco das coordenadas'}>
                <span>
                  <IconButton
                    onClick={onRefreshAddress}
                    disabled={geocodingLoading || !data.latitude || !data.longitude}
                    sx={{
                      color: colors.accent,
                      '&:hover': { backgroundColor: colors.accentSubtle },
                    }}
                  >
                    {geocodingLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PremiumInput
              value={data.city || ''}
              onChange={(e) => updateData({ city: e.target.value })}
              label={t('poiCity') || 'Cidade'}
              disabled={geocodingLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <PremiumInput
              value={data.state || ''}
              onChange={(e) => updateData({ state: e.target.value })}
              label={t('poiState') || 'Estado'}
              disabled={geocodingLoading}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <PremiumInput
              value={data.postalCode || ''}
              onChange={(e) => updateData({ postalCode: e.target.value })}
              label={t('poiPostalCode') || 'CEP'}
              disabled={geocodingLoading}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Secao Contato */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('poiContact') || 'Contato'}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <PremiumInput
              value={data.contactName || ''}
              onChange={(e) => updateData({ contactName: e.target.value })}
              label={t('poiContactName') || 'Nome do Contato'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PremiumInput
              value={data.contactPhone || ''}
              onChange={(e) => updateData({ contactPhone: e.target.value })}
              label={t('poiContactPhone') || 'Telefone'}
              placeholder="(XX) XXXXX-XXXX"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <PremiumInput
              type="email"
              value={data.contactEmail || ''}
              onChange={(e) => updateData({ contactEmail: e.target.value })}
              label={t('poiContactEmail') || 'E-mail'}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 6: REVISAO
// ============================================================================

const ReviewStep = ({ data, groups, selectedIcon }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0 && value !== false) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {typeof value === 'boolean' ? (value ? t('sharedYes') : t('sharedNo')) : value}
        </Typography>
      </Box>
    );
  };

  const group = groups.find((g) => g.id === data.groupId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      {/* Preview do marcador */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 3,
          backgroundColor: colors.surfaceHover,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <PoiMarkerPreview
          icon={selectedIcon}
          color={data.color || '#3B82F6'}
          name={data.name}
          size="large"
        />
      </Box>

      {/* Informacoes Basicas */}
      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('positionLatitude')} value={data.latitude?.toFixed(6)} />
        <InfoRow label={t('positionLongitude')} value={data.longitude?.toFixed(6)} />
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />
        <InfoRow label={t('sharedGroup')} value={group?.name || '-'} />
        <InfoRow label={t('poiFavorite') || 'Favorito'} value={data.favorite} />
        <InfoRow label={t('sharedShow')} value={data.visible !== false} />
        <InfoRow label={t('poiRadius') || 'Raio'} value={`${data.radius || 100}m`} />
      </Box>

      {/* Endereco e Contato */}
      {(data.address || data.city || data.contactName || data.contactPhone) && (
        <Box
          sx={{
            backgroundColor: colors.surfaceElevated,
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${colors.borderSubtle}`,
            p: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '11px',
              letterSpacing: '0.05em',
              mb: 2,
            }}
          >
            {t('poiAddress')} & {t('poiContact')}
          </Typography>
          <InfoRow label={t('poiAddressFull') || 'Endereco'} value={data.address} />
          <InfoRow label={t('poiCity') || 'Cidade'} value={data.city} />
          <InfoRow label={t('poiState') || 'Estado'} value={data.state} />
          <InfoRow label={t('poiPostalCode') || 'CEP'} value={data.postalCode} />
          <InfoRow label={t('poiContactName') || 'Contato'} value={data.contactName} />
          <InfoRow label={t('poiContactPhone') || 'Telefone'} value={data.contactPhone} />
          <InfoRow label={t('poiContactEmail') || 'E-mail'} value={data.contactEmail} />
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PoiFormWizard = ({ poi, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Estado para icons, groups e icon selector
  const [icons, setIcons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const lastGeocodedRef = useRef({ lat: null, lng: null });

  // Fetch icons and groups
  useEffectAsync(async () => {
    try {
      const [iconsRes, groupsRes] = await Promise.all([
        fetchOrThrow('/api/poiicons'),
        fetchOrThrow('/api/poigroups'),
      ]);
      const iconsData = await iconsRes.json();
      const groupsData = await groupsRes.json();
      setIcons(iconsData);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  // Atualiza selectedIcon quando poi carrega (edicao)
  useEffect(() => {
    if (poi && !selectedIcon) {
      if (poi.iconType === 'lucide' && poi.iconCode) {
        setSelectedIcon({
          iconType: 'lucide',
          iconCode: poi.iconCode,
        });
      } else if (poi.iconId && icons.length > 0) {
        const icon = icons.find((i) => i.id === poi.iconId);
        if (icon) {
          setSelectedIcon({
            iconType: 'png',
            path: icon.path,
            name: icon.name,
          });
        }
      }
    }
  }, [poi, icons, selectedIcon]);

  // Funcao para buscar endereco via reverse geocoding
  const fetchAddress = useCallback(async (latitude, longitude, forceUpdate = false, updateDataFn) => {
    if (latitude == null || longitude == null) return;

    const { lat, lng } = lastGeocodedRef.current;
    if (!forceUpdate && lat === latitude && lng === longitude) return;

    setGeocodingLoading(true);
    try {
      const addressData = await reverseGeocode(latitude, longitude);
      lastGeocodedRef.current = { lat: latitude, lng: longitude };

      if (updateDataFn) {
        updateDataFn({
          address: addressData.address || '',
          city: addressData.city || '',
          state: addressData.state || '',
          postalCode: addressData.postalCode || '',
        });
      }
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
    } finally {
      setGeocodingLoading(false);
    }
  }, []);

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardPoiBasicDescription') || 'Nome e coordenadas do POI',
      icon: <InfoIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (data.latitude === undefined || data.latitude === null || data.latitude === '') {
          errors.latitude = t('sharedRequired');
        }
        if (data.longitude === undefined || data.longitude === null || data.longitude === '') {
          errors.longitude = t('sharedRequired');
        }
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('positionLatitude'), value: data.latitude?.toFixed(4) },
      ],
    },
    {
      label: t('sharedGroup') || 'Grupo',
      title: t('sharedGroup') || 'Grupo e Visibilidade',
      description: t('wizardPoiGroupDescription') || 'Organizacao e visibilidade',
      icon: <CategoryIcon sx={{ fontSize: 18 }} />,
      component: (props) => <GroupStep {...props} groups={groups} />,
      optional: true,
    },
    {
      label: t('poiAppearance') || 'Aparencia',
      title: t('poiAppearance') || 'Aparencia',
      description: t('wizardPoiAppearanceDescription') || 'Cor, icone e raio',
      icon: <PaletteIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <AppearanceStep
          {...props}
          icons={icons}
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
          iconSelectorOpen={iconSelectorOpen}
          setIconSelectorOpen={setIconSelectorOpen}
        />
      ),
      optional: true,
    },
    {
      label: t('sharedLocation') || 'Localizacao',
      title: t('sharedLocation') || 'Localizacao',
      description: t('wizardPoiLocationDescription') || 'Posicao no mapa',
      icon: <MapIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <LocationStep
          {...props}
          onLocationChange={({ latitude, longitude }) => {
            props.updateData({ latitude, longitude });
            fetchAddress(latitude, longitude, false, props.updateData);
          }}
        />
      ),
      optional: true,
    },
    {
      label: t('poiAddress') || 'Endereco',
      title: `${t('poiAddress') || 'Endereco'} & ${t('poiContact') || 'Contato'}`,
      description: t('wizardPoiAddressDescription') || 'Endereco e informacoes de contato',
      icon: <ContactPhoneIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <AddressContactStep
          {...props}
          geocodingLoading={geocodingLoading}
          onRefreshAddress={() => fetchAddress(props.data.latitude, props.data.longitude, true, props.updateData)}
        />
      ),
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: (props) => (
        <ReviewStep
          {...props}
          groups={groups}
          selectedIcon={selectedIcon}
        />
      ),
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = poi?.id ? `/api/pois/${poi.id}` : '/api/pois';
      const method = poi?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const savedPoi = await response.json();
        dispatch(poisActions.update([savedPoi]));
        onSave?.(savedPoi);
        navigate('/settings/pois');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/pois');
  };

  const initialData = poi || {
    name: '',
    description: '',
    latitude: null,
    longitude: null,
    groupId: null,
    favorite: false,
    visible: true,
    color: '#3B82F6',
    iconType: null,
    iconId: null,
    iconCode: null,
    radius: 100,
    address: '',
    city: '',
    state: '',
    postalCode: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={poi?.id ? null : 'poi-wizard-draft'}
      completeButtonText={poi?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default PoiFormWizard;
